// frontend/src/Login.js
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "./api";
import { Preferences } from "@capacitor/preferences"; // add

export default function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loginMessage, setLoginMessage] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [credsLoaded, setCredsLoaded] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [setupEmail, setSetupEmail] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setupPassword2, setSetupPassword2] = useState("");

  // Prefill saved credentials (iOS Preferences -> Keychain under the hood)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [savedEmail, savedPassword] = await Promise.all([
          Preferences.get({ key: "savedEmail" }),
          Preferences.get({ key: "savedPassword" }),
        ]);
        if (!mounted) return;
        if (savedEmail?.value) setEmail(savedEmail.value);
        if (savedPassword?.value) setPassword(savedPassword.value);
      } catch (e) {
        console.warn("Could not load saved credentials:", e);
      } finally {
        if (mounted) setCredsLoaded(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoginMessage("");
    setBusy(true);

    // Show warming message after 2 seconds if still loading
    const warmingTimeout = setTimeout(() => {
      if (busy) {
        setLoginMessage("Waking up the server, this may take a moment...");
      }
    }, 2000);

    try {
      const startTime = Date.now();
      const payload = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
        skipAuthRedirect: true,
      });
      const elapsed = Date.now() - startTime;

      // If it took more than 3 seconds, the server was likely warming up
      if (elapsed > 3000) {
        console.log(`Login took ${Math.round(elapsed / 1000)}s - server was warming up`);
      }

      clearTimeout(warmingTimeout);

      // First-time user: no password set yet, redirect to setup
      if (payload?.needs_setup) {
        setSetupEmail(payload.email || email);
        setSetupMode(true);
        setBusy(false);
        setLoginMessage("");
        return;
      }

      const { token, user } = payload;

      // Save securely for mobile (and keep web fallback)
      await Preferences.set({ key: "authToken", value: token });
      localStorage.setItem("authToken", token);
      localStorage.setItem("coachToken", token); // legacy compatibility
      localStorage.setItem("currentUser", JSON.stringify(user));

      // Save login for next time if opted in (iOS stores this in Keychain)
      if (rememberMe) {
        try {
          await Preferences.set({ key: "savedEmail", value: email });
          await Preferences.set({ key: "savedPassword", value: password });
        } catch (e) {
          console.warn("Could not persist credentials:", e);
        }
      } else {
        await Preferences.remove({ key: "savedEmail" });
        await Preferences.remove({ key: "savedPassword" });
      }

      if (onSuccess) onSuccess(user);
      else window.location.href = "/";
    } catch (err) {
      clearTimeout(warmingTimeout);
      const msg = err?.message || "";
      setError(
        msg.toLowerCase().includes("invalid credentials")
          ? "Invalid login credentials. Please check your email and password, or use Forgot Password to reset your password."
          : msg || "Login failed"
      );
    } finally {
      setBusy(false);
      setLoginMessage("");
    }
  }

  async function submitSetup(e) {
    e.preventDefault();
    setError("");
    if (setupPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (setupPassword !== setupPassword2) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const payload = await apiFetch("/auth/setup", {
        method: "POST",
        body: JSON.stringify({ email: setupEmail, password: setupPassword }),
        skipAuthRedirect: true,
        maxRetries: 0,
      });
      const { token, user } = payload;
      await Preferences.set({ key: "authToken", value: token });
      localStorage.setItem("authToken", token);
      localStorage.setItem("coachToken", token);
      localStorage.setItem("currentUser", JSON.stringify(user));
      if (onSuccess) onSuccess(user);
      else window.location.href = "/";
    } catch (err) {
      setError(err?.message || "Failed to set up account");
    } finally {
      setBusy(false);
    }
  }

  if (!credsLoaded) return null;

  if (setupMode) {
    return (
      <div className="stack">
        <div className="brand">
          <img src="/logo.png" alt="TBCRacing logo" className="brand-logo-lg" />
        </div>
        <form className="stack" onSubmit={submitSetup}>
          <h2 className="h2">Finish setting up your account</h2>
          <p className="help">Welcome! Create a password to get started.</p>
          <input
            className="input"
            type="email"
            value={setupEmail}
            readOnly
            style={{ color: "#888" }}
          />
          <input
            className="input"
            type="password"
            placeholder="New password (min 8 chars)"
            value={setupPassword}
            onChange={(e) => setSetupPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          <input
            className="input"
            type="password"
            placeholder="Confirm password"
            value={setupPassword2}
            onChange={(e) => setSetupPassword2(e.target.value)}
            autoComplete="new-password"
            required
          />
          {error && <div className="help" style={{ color: "red" }}>{error}</div>}
          <button className="btn" type="submit" disabled={busy}>
            {busy ? "Setting up…" : "Create password & continue"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="brand">
          <img src="/logo.png" alt="TBCRacing logo" className="brand-logo-lg" />
        </div>
        <div> <p className="help">Welcome to TBC Racing's Team App. <br/>Login here to join the Family!</p></div>
      <form className="stack" onSubmit={submit}>
        <h2 className="h2">Login</h2>

        <input
          className="input"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />

        <input
          className="input"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          Remember me on this device
        </label>

        {error && <div className="help" style={{ color: "red" }}>{error}</div>}
        {loginMessage && <div className="help" style={{ color: "#f59e0b", fontWeight: 500 }}>{loginMessage}</div>}

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <div className="help" style={{ textAlign: "center", marginTop: 8 }}>
          <Link to="/reset">Forgot password?</Link>
        </div>

        <div className="help" style={{ textAlign: "center", marginTop: 8 }}>
          <a
            href="https://apps.apple.com/app/tbc-racing-team-app/id6754887030"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download the iPhone app
          </a>
        </div>
      </form>
    </div>
  );
}
